import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, ensureIdentity, tokens } from '../api/client';
import {
  useActiveSessions,
  useAddVehicle,
  useDeleteVehicle,
  useLot,
  usePatchVehicle,
  useSetDefaultVehicle,
  useStates,
  useVehicles,
} from '../api/hooks';
import type { SessionView, Vehicle } from '../api/types';
import { TopBar } from '../components/chrome';
import { IconRadioOff, IconRadioOn } from '../components/icons';
import {
  Card,
  Cta,
  ErrorState,
  LinkButton,
  Loading,
  Muted,
  Note,
  Screen,
  SecondaryButton,
} from '../components/ui';
import { useApiError } from '../i18n/useApiError';
import { countdown, plateDisplay } from '../lib/format';
import { loadRecentStates, orderStateGroups, recordRecentState } from '../lib/recent-states';

/** Upper-case, no separators, letters and digits only (FR-U-07). */
const normalize = (raw: string) => raw.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * Plate selection and management.
 *
 * A wrong plate means the driver paid and still gets ticketed, so the plate is
 * large, the default is marked, and any plate already parked elsewhere is
 * flagged before they commit (FR-U-05 / 08). A plate parked at *this* lot
 * routes into its running session instead of a fresh checkout — the extend
 * flow, not a second payment, is the primary path for it.
 */
export function PlatesPage() {
  const { t } = useTranslation();
  const { lotId } = useParams({ from: '/lot/$lotId/plates' });
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [managing, setManaging] = useState(false);
  const [spaceNumber, setSpaceNumber] = useState('');
  const lot = useLot(lotId);

  // No sign-in wall: paying is the product, an account is optional. A
  // first-timer gets an anonymous one created here, before the plate list is
  // fetched, so the rest of the flow has an identity to hang vehicles off.
  const [ready, setReady] = useState(tokens.signedIn);
  const vehicles = useVehicles(ready);
  const sessions = useActiveSessions(ready);
  useEffect(() => {
    if (tokens.signedIn) return;
    void ensureIdentity()
      .then(() => setReady(true))
      .catch(() => setReady(true));
  }, []);

  if (!ready) {
    return (
      <>
        <TopBar title={t('plates.title')} />
        <Screen>
          <Loading />
        </Screen>
      </>
    );
  }

  if (vehicles.isPending) {
    return (
      <>
        <TopBar title={t('plates.title')} />
        <Screen>
          <Loading />
        </Screen>
      </>
    );
  }

  if (vehicles.isError) {
    return (
      <>
        <TopBar title={t('plates.title')} />
        <Screen>
          <ErrorState message={t('plates.loadFailed')} onRetry={() => void vehicles.refetch()} />
        </Screen>
      </>
    );
  }

  const list = vehicles.data.vehicles;
  const active = sessions.data?.sessions ?? [];
  // Plate and issuing state together identify the car; the plate number alone
  // collides across states.
  const sessionFor = (vehicle: Vehicle) =>
    active.find((s) => s.plate === vehicle.plate && s.plateState === vehicle.state);
  const chosen = selected ?? list.find((v) => v.isDefault)?.id ?? list[0]?.id ?? null;
  const chosenVehicle = list.find((v) => v.id === chosen);
  const chosenSession = chosenVehicle ? sessionFor(chosenVehicle) : undefined;
  // Only a session at this lot re-routes; one elsewhere still flags on the row
  // and again at confirm, where the quote's duplicateWarning gates payment.
  const sessionHere = chosenSession && chosenSession.lot.id === lotId ? chosenSession : undefined;
  const numericSpaceNumber = Number(spaceNumber);
  const spaceNumberValid =
    lot.data?.spaceNumberingEnabled === false ||
    (/^\d{4}$/.test(spaceNumber) &&
      numericSpaceNumber >= (lot.data?.spaceNumberStart ?? Number.POSITIVE_INFINITY) &&
      numericSpaceNumber <= (lot.data?.spaceNumberEnd ?? Number.NEGATIVE_INFINITY));

  return (
    <>
      <TopBar title={t('plates.title')} />
      <Screen>
        {/* The top bar already carries the page title; the body keeps only
            the subtitle (review §5, no repeated titles). */}
        <Muted>{t('plates.subtitle')}</Muted>

        {!sessionHere && lot.data?.spaceNumberingEnabled !== false ? (
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold">{t('plates.spaceNumberLabel')}</span>
            <input
              className="num min-h-12 rounded-lg border border-hairline px-3 text-xl font-semibold"
              value={spaceNumber}
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              placeholder={lot.data ? String(lot.data.spaceNumberStart) : '1000'}
              onChange={(event) => setSpaceNumber(event.target.value.replace(/\D/g, '').slice(0, 4))}
            />
            <span className={`text-xs ${spaceNumber && !spaceNumberValid ? 'text-danger' : 'text-muted'}`}>
              {lot.data
                ? t(
                    spaceNumber && !spaceNumberValid ? 'plates.spaceNumberInvalid' : 'plates.spaceNumberHint',
                    {
                      start: lot.data.spaceNumberStart,
                      end: lot.data.spaceNumberEnd,
                    },
                  )
                : t('plates.spaceNumberLoading')}
            </span>
          </label>
        ) : null}

        <div className="flex flex-col gap-2.5">
          {list.map((vehicle) => {
            if (managing) {
              return <ManageRow key={vehicle.id} vehicle={vehicle} session={sessionFor(vehicle)} />;
            }
            const busy = sessionFor(vehicle);
            const isChosen = vehicle.id === chosen;
            return (
              <button
                key={vehicle.id}
                type="button"
                aria-pressed={isChosen}
                onClick={() => setSelected(vehicle.id)}
                className={`flex items-center gap-4 rounded-lg border p-4 text-left ${
                  isChosen ? 'border-2 border-ink' : 'border-[1.5px] border-hairline'
                }`}
              >
                <span className="flex flex-1 flex-col gap-1">
                  <span className="num text-2xl font-semibold tracking-[2px]">
                    {plateDisplay(vehicle.plate)}
                  </span>
                  <span className="text-xs text-muted">
                    {vehicle.state}
                    {vehicle.label ? ` · ${vehicle.label}` : ''}
                    {vehicle.isDefault ? ` · ${t('plates.default')}` : ''}
                  </span>
                  {busy ? (
                    <span className="num w-fit rounded-md bg-ink px-2 py-0.5 text-[11px] text-white">
                      {t(busy.lot.id === lotId ? 'plates.parkedHere' : 'plates.inProgress', {
                        remaining: countdown(busy.remainingSeconds),
                      })}
                    </span>
                  ) : null}
                </span>
                <span className={isChosen ? 'text-accent' : 'text-hairline-strong'}>
                  {isChosen ? <IconRadioOn className="h-6 w-6" /> : <IconRadioOff className="h-6 w-6" />}
                </span>
              </button>
            );
          })}
        </div>

        {managing ? (
          <SecondaryButton label={t('plates.done')} onClick={() => setManaging(false)} />
        ) : adding ? (
          <AddPlateForm onDone={() => setAdding(false)} />
        ) : (
          <>
            <SecondaryButton dashed label={t('plates.add')} onClick={() => setAdding(true)} />
            {list.length > 0 ? (
              <SecondaryButton label={t('plates.manage')} onClick={() => setManaging(true)} />
            ) : null}
            {/* An active session at this lot owns the primary action: review /
                extend it. The fresh-checkout continue only renders otherwise,
                so there is never a one-tap path into paying twice here. */}
            {sessionHere ? (
              <Cta
                label={t('plates.viewSession')}
                note={t('plates.viewSessionNote', {
                  remaining: countdown(sessionHere.remainingSeconds),
                })}
                onClick={() =>
                  void navigate({ to: '/sessions/$sessionId', params: { sessionId: sessionHere.id } })
                }
              />
            ) : (
              <Cta
                label={t('common.continue')}
                note={t('plates.nextStep')}
                disabled={!chosen || !spaceNumberValid}
                onClick={() =>
                  chosen &&
                  void navigate({
                    to: '/lot/$lotId/duration',
                    params: { lotId },
                    search: {
                      vehicleId: chosen,
                      spaceNumber: lot.data?.spaceNumberingEnabled === false ? '' : spaceNumber,
                    },
                  })
                }
              />
            )}
          </>
        )}
      </Screen>
    </>
  );
}

/**
 * One plate in manage mode: edit (issuing state and label), set default, and
 * delete. The plate NUMBER is deliberately not editable — sessions snapshot it
 * and the promo lock window keys off it — so delete-and-re-add is the honest
 * correction for a mistyped plate, and the edit form says so.
 */
function ManageRow({ vehicle, session }: { vehicle: Vehicle; session: SessionView | undefined }) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const setDefault = useSetDefaultVehicle();
  const deleteVehicle = useDeleteVehicle();
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (mutate: () => Promise<unknown>) => {
    setError(null);
    try {
      await mutate();
    } catch (e) {
      setError(describeError(e, 'plates.actionFailed'));
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border-[1.5px] border-hairline p-4">
      <div className="flex flex-col gap-1">
        <span className="num text-2xl font-semibold tracking-[2px]">{plateDisplay(vehicle.plate)}</span>
        <span className="text-xs text-muted">
          {vehicle.state}
          {vehicle.label ? ` · ${vehicle.label}` : ''}
          {vehicle.isDefault ? ` · ${t('plates.default')}` : ''}
        </span>
        {session ? (
          <span className="num w-fit rounded-md bg-ink px-2 py-0.5 text-[11px] text-white">
            {t('plates.inProgress', { remaining: countdown(session.remainingSeconds) })}
          </span>
        ) : null}
      </div>

      {editing ? (
        <EditPlateForm vehicle={vehicle} onDone={() => setEditing(false)} />
      ) : confirming ? (
        <div className="flex flex-col gap-2.5 border-t border-hairline pt-3">
          <p className="text-[13px] leading-relaxed text-danger">
            {t('plates.deleteConfirm', { plate: plateDisplay(vehicle.plate) })}
          </p>
          <button
            type="button"
            disabled={deleteVehicle.isPending}
            onClick={() => void run(() => deleteVehicle.mutateAsync(vehicle.id))}
            className="min-h-11 w-full rounded-lg bg-danger px-4 text-[15px] font-semibold text-white disabled:opacity-40"
          >
            {t('plates.deleteConfirmYes')}
          </button>
          <LinkButton label={t('common.cancel')} onClick={() => setConfirming(false)} />
        </div>
      ) : (
        <div className="flex gap-2.5">
          <button
            type="button"
            onClick={() => {
              setError(null);
              setEditing(true);
            }}
            className="min-h-11 flex-1 rounded-lg border-[1.5px] border-hairline px-3 text-[13px] font-semibold text-accent"
          >
            {t('plates.edit')}
          </button>
          {!vehicle.isDefault ? (
            <button
              type="button"
              disabled={setDefault.isPending}
              onClick={() => void run(() => setDefault.mutateAsync(vehicle.id))}
              className="min-h-11 flex-1 rounded-lg border-[1.5px] border-hairline px-3 text-[13px] font-semibold text-accent disabled:opacity-40"
            >
              {t('plates.setDefault')}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              // The server refuses this too (409 vehicle_in_use), but the
              // check stays client-side as well: it gives the driver the
              // reason without a round trip, and still holds against a
              // deployed API that predates the server guard.
              if (session) {
                setError(t('plates.deleteBlockedActive'));
              } else {
                setError(null);
                setConfirming(true);
              }
            }}
            className="min-h-11 flex-1 rounded-lg border-[1.5px] border-hairline px-3 text-[13px] font-semibold text-danger"
          >
            {t('plates.delete')}
          </button>
        </div>
      )}

      {error ? <p className="text-[13px] text-danger">{error}</p> : null}
    </div>
  );
}

/**
 * Corrects a plate's issuing state and label in place. The plate number is
 * absent on purpose (see ManageRow); the form states the rule instead of
 * silently lacking the field.
 */
function EditPlateForm({ vehicle, onDone }: { vehicle: Vehicle; onDone: () => void }) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const patchVehicle = usePatchVehicle();
  const [state, setState] = useState(vehicle.state);
  const [label, setLabel] = useState(vehicle.label ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    // Strict server body: only the fields that actually changed go up.
    const nextLabel = label.trim() === '' ? null : label.trim();
    const patch: { state?: string; label?: string | null } = {};
    if (state !== vehicle.state) patch.state = state;
    if (nextLabel !== (vehicle.label ?? null)) patch.label = nextLabel;
    if (Object.keys(patch).length === 0) {
      onDone();
      return;
    }
    setError(null);
    try {
      await patchVehicle.mutateAsync({ vehicleId: vehicle.id, ...patch });
      if (patch.state) recordRecentState(patch.state);
      onDone();
    } catch (e) {
      // The deployed API may predate PATCH /vehicles/:id. A 404 whose code is
      // not vehicle_not_found means the endpoint itself is missing, and the
      // generic route_not_found copy ("reload and try again") would be a lie —
      // say honestly that editing isn't available yet.
      if (e instanceof ApiError && e.status === 404 && e.code !== 'vehicle_not_found') {
        setError(t('plates.editUnavailable'));
      } else {
        setError(describeError(e, 'plates.saveFailed'));
      }
    }
  };

  return (
    <div className="flex flex-col gap-2.5 border-t border-hairline pt-3">
      <div className="flex flex-col gap-2">
        <Muted>{t('plates.stateLabel')}</Muted>
        <StateSelect value={state} onChange={setState} />
      </div>
      <div className="flex flex-col gap-2">
        <Muted>{t('plates.labelLabel')}</Muted>
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder={t('plates.labelPlaceholder')}
          aria-label={t('plates.labelLabel')}
          maxLength={32}
          className="min-h-[48px] rounded-lg border-[1.5px] border-hairline px-3 text-[15px]"
        />
      </div>
      <Muted>{t('plates.plateLocked')}</Muted>
      <button
        type="button"
        disabled={patchVehicle.isPending}
        onClick={() => void save()}
        className="min-h-11 w-full rounded-lg bg-accent px-4 text-[15px] font-semibold text-white disabled:opacity-40"
      >
        {t('plates.saveChanges')}
      </button>
      <LinkButton label={t('common.cancel')} onClick={onDone} />
      {error ? <p className="text-[13px] text-danger">{error}</p> : null}
    </div>
  );
}

function AddPlateForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const addVehicle = useAddVehicle();
  const [plate, setPlate] = useState('');
  const [state, setState] = useState('IL');
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    try {
      await addVehicle.mutateAsync({ plate, state });
      recordRecentState(state);
      onDone();
    } catch (e) {
      setError(describeError(e, 'plates.saveFailed'));
    }
  };

  return (
    <Card>
      <p className="text-[15px] font-semibold">{t('plates.addTitle')}</p>
      <input
        value={plate}
        onChange={(event) => setPlate(normalize(event.target.value))}
        placeholder="AB12345"
        aria-label={t('plates.plateLabel')}
        autoCapitalize="characters"
        autoCorrect="off"
        maxLength={10}
        className="num min-h-[64px] rounded-lg border-[1.5px] border-ink px-4 text-center text-[30px] tracking-[4px] outline-none"
      />
      <Muted>{t('plates.normalizeHint')}</Muted>

      <div className="flex flex-col gap-2">
        <Muted>{t('plates.stateLabel')}</Muted>
        <StateSelect value={state} onChange={setState} />
      </div>

      {error ? <p className="text-[13px] text-danger">{error}</p> : null}

      <Note>{t('plates.plateWarning')}</Note>

      <Cta
        label={t('plates.save')}
        note={t('plates.savedState', { state })}
        loading={addVehicle.isPending}
        disabled={plate.length < 2}
        onClick={() => void save()}
      />
      <LinkButton label={t('common.cancel')} onClick={onDone} />
    </Card>
  );
}

/**
 * The one issuing-state picker, grouped nearby / recent / other. Shared by add
 * and edit so both order states from the same server-priority source. The
 * current value is always kept selectable, even when the states endpoint is
 * unreachable and the offline fallback list doesn't contain it.
 */
function StateSelect({ value, onChange }: { value: string; onChange: (state: string) => void }) {
  const { t } = useTranslation();
  const states = useStates();
  // Read once per mount: the list only changes on save, which closes the form.
  const [recent] = useState(loadRecentStates);

  const fallback = ['IL', 'IN', 'WI', 'MI', 'IA'];
  const priority = states.data?.priority ?? fallback;
  const known = states.data?.all ?? fallback;
  const all = known.includes(value) ? known : [...known, value];
  const groups = orderStateGroups(priority, all, recent);

  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label={t('plates.stateLabel')}
      className="min-h-[48px] rounded-lg border-[1.5px] border-hairline px-3 text-[15px]"
    >
      <optgroup label={t('plates.statesNearby')}>
        {groups.priority.map((code) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </optgroup>
      {groups.recent.length > 0 ? (
        <optgroup label={t('plates.statesRecent')}>
          {groups.recent.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </optgroup>
      ) : null}
      {groups.rest.length > 0 ? (
        <optgroup label={t('plates.statesOther')}>
          {groups.rest.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </optgroup>
      ) : null}
    </select>
  );
}
